import { createApi } from '@reduxjs/toolkit/dist/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { HTTP_METHOD, REDUX_CACHE, TRAININGS } from '../constants';
import { ITraining } from '../../../types/interfaces/training';
import { ITrainingSetItem } from '../../../types/interfaces/training-set-item';
import { TrainingStatus } from '../../../shared/constants';

export const trainingsApi = createApi({
  reducerPath: 'trainingsApi',
  tagTypes: [
    TRAININGS.TRAININGS,
    TRAININGS.TRAINING_ITEMS,
    TRAININGS.CURRENT,
    TRAININGS.AVAILABLE,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserTrainingsApi: builder.query<
      ITraining[],
      {
        includeItems?: boolean;
        includeAvailableToUsers?: boolean;
      } | void
    >({
      keepUnusedDataFor: 3600,
      query(args) {
        const queryString: string[] = [];

        if (typeof args === 'object' && args?.includeItems) {
          queryString.push('includeItems=true');
        }

        if (typeof args === 'object' && args?.includeAvailableToUsers) {
          queryString.push('includeAvailableToUsers=true');
        }

        return {
          url: `${endpoints.Training.ROOT}${queryString.length ? '?' + queryString.join('&') : ''}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRAININGS.TRAININGS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAvailableUserTrainingsApi: builder.query<ITraining[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Training.AVAILABLE,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRAININGS.AVAILABLE),
      ...TransformedErrorResponseWithNotification(),
    }),
    getTrainingByIdApi: builder.query<ITraining, string>({
      keepUnusedDataFor: 3600,
      query(id) {
        return {
          url: `${endpoints.Training.ROOT}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [{ type: TRAININGS.TRAININGS, id }];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getCurrentTrainingApi: builder.query<ITraining, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Training.CURRENT,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error) => [{ type: TRAININGS.CURRENT }],
      // ...TransformedErrorResponseWithNotification()
    }),
    postPutTraining: builder.mutation<void, { id?: string; body: ITraining }>({
      query({ id, body }) {
        return {
          url: endpoints.Training.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.CURRENT,
            },
            {
              type: TRAININGS.TRAININGS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Training'),
    }),
    nextTrainingStatusApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: endpoints.Training.NEXT_STATUS.replace('${0}', id),
          method: HTTP_METHOD.PUT,
        };
      },
      invalidatesTags: (result, error, id) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.CURRENT,
            },
            {
              type: TRAININGS.TRAININGS,
              id: id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    // updateTrainingItemStatusApi: builder.mutation<
    //   void,
    //   { trainingId: string; trainingItemId: string; status: TrainingStatus }
    // >({
    //   query({ trainingId, trainingItemId, status }) {
    //     return {
    //       url: endpoints.Training.ITEM_STATUS.replace(
    //         '${0}',
    //         trainingId,
    //       ).replace('${1}', trainingItemId),
    //       method: HTTP_METHOD.PUT,
    //       body: { status },
    //     };
    //   },
    //   invalidatesTags: (result, error, params) => {
    //     if (error) {
    //       return [REDUX_CACHE.ABORT_INVALIDATION];
    //     } else {
    //       return [
    //         {
    //           type: TRAININGS.TRAININGS,
    //           id: REDUX_CACHE.LIST,
    //         },
    //         {
    //           type: TRAININGS.CURRENT,
    //         },
    //         {
    //           type: TRAININGS.TRAININGS,
    //           id: params.trainingId,
    //         },
    //       ];
    //     }
    //   },
    //   ...TransformedErrorResponseWithNotification(),
    // }),

    putTrainingSetItem: builder.mutation<
      void,
      {
        trainingId: string;
        trainingSetItemId: string;
        body: ITrainingSetItem;
      }
    >({
      query({ body, trainingId, trainingSetItemId }) {
        return {
          url: endpoints.Training.ITEM.replace('${0}', trainingId).replace(
            '${1}',
            trainingSetItemId,
          ),
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.TRAININGS,
              id: params.trainingId,
            },
            {
              type: TRAININGS.CURRENT,
            },
            { type: TRAININGS.CURRENT },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteTraining: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Training.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    shareTraining: builder.mutation<
      void,
      { trainingId: string; userId: string }
    >({
      query({ trainingId, userId }) {
        return {
          url: endpoints.Training.USER.replace('${0}', trainingId).replace(
            '${1}',
            userId,
          ),
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.AVAILABLE,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Training'),
    }),
    unshareTraining: builder.mutation<
      void,
      { trainingId: string; userId: string }
    >({
      query({ trainingId, userId }) {
        return {
          url: endpoints.Training.USER.replace('${0}', trainingId).replace(
            '${1}',
            userId,
          ),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRAININGS.TRAININGS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: TRAININGS.AVAILABLE,
              id: params.trainingId,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Training'),
    }),
  }),
});

export const {
  useGetAvailableUserTrainingsApiQuery: useGetAvailableUserTrainings,
  useGetUserTrainingsApiQuery: useGetUserTrainings,
  useGetTrainingByIdApiQuery: useGetTrainingById,
  useLazyGetTrainingByIdApiQuery: useLazyGetTrainingById,
  useGetCurrentTrainingApiQuery: useGetCurrentTraining,
  usePostPutTrainingMutation: usePostPutTraining,
  useNextTrainingStatusApiMutation: useNextTrainingStatus,
  // useUpdateTrainingItemStatusApiMutation: useUpdateTrainingItemStatus,
  usePutTrainingSetItemMutation: usePutTrainingSetItem,
  useDeleteTrainingMutation: useDeleteTraining,
  useShareTrainingMutation: useShareTraining,
  useUnshareTrainingMutation: useUnshareTraining,
} = trainingsApi;
